import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkPublishItemsApplicationErrors,
  BulkPublishItemsOptions,
  BulkPublishItemsResponse,
  CancelScheduledItemApplicationErrors,
  CancelScheduledItemResponse,
  CreateDraftItemOptions,
  DeleteItemApplicationErrors,
  GetItemsResponse,
  Item,
  ItemPublishFailedEnvelope,
  ItemPublishScheduledEnvelope,
  ItemPublishStartedEnvelope,
  ItemPublishedEnvelope,
  PublishItemApplicationErrors,
  PublishItemByIdApplicationErrors,
  PublishItemByIdOptions,
  PublishItemByIdResponse,
  PublishItemOptions,
  PublishItemResponse,
  PublishMultiStoryDraftApplicationErrors,
  PublishMultiStoryDraftOptions,
  PublishMultiStoryDraftResponse,
  Query,
  QueryItemsResponse,
  RescheduleItemApplicationErrors,
  RescheduleItemOptions,
  RescheduleItemResponse,
  UpdateItem,
  UpdateItemApplicationErrors,
  bulkPublishItems as universalBulkPublishItems,
  cancelScheduledItem as universalCancelScheduledItem,
  createDraftItem as universalCreateDraftItem,
  deleteItem as universalDeleteItem,
  getItem as universalGetItem,
  getItems as universalGetItems,
  publishItem as universalPublishItem,
  publishItemById as universalPublishItemById,
  publishMultiStoryDraft as universalPublishMultiStoryDraft,
  queryItems as universalQueryItems,
  rescheduleItem as universalRescheduleItem,
  updateItem as universalUpdateItem,
} from './promote-publisher-v1-item-items.universal.js';

export const __metadata = {
  PACKAGE_NAME: '@wix/promote-growth-tools-publisher',
};

export function publishItem(httpClient: HttpClient): PublishItemSignature {
  return (
    options?: NonNullablePaths<
      PublishItemOptions,
      `item.channel` | `item.channel.accountId`,
      2
    >
  ) =>
    universalPublishItem(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface PublishItemSignature {
  /**
   * Publishes an item to a connected social channel.
   *
   * Use this method to publish a fully-formed post in a single call. To publish a previously created draft, call Publish Item By ID. To save a post without publishing it, call Create Draft Item. To schedule the post, set `item.schedulingInfo.scheduledDate`; otherwise it's published immediately.
   *
   * Published items appear on the site's Social Media Marketing page in the dashboard.
   */
  (
    options?: NonNullablePaths<
      PublishItemOptions,
      `item.channel` | `item.channel.accountId`,
      2
    >
  ): Promise<
    NonNullablePaths<
      PublishItemResponse,
      | `item.instagramPost.mediaWrapper.media`
      | `item.instagramPost.mediaWrapper.media.${number}.type`
      | `item.instagramPost.insights.likes`
      | `item.instagramPost.insights.engagement`
      | `item.instagramPost.insights.impressions`
      | `item.instagramPost.insights.plays`
      | `item.instagramPost.insights.reach`
      | `item.instagramPost.insights.comments`
      | `item.instagramPost.insights.shares`
      | `item.instagramPost.insights.saved`
      | `item.instagramPost.insights.totalInteractions`
      | `item.instagramStory.insights.impressions`
      | `item.instagramStory.insights.reach`
      | `item.instagramStory.insights.replies`
      | `item.facebookPost.insights.impressions.unique`
      | `item.facebookPost.insights.impressions.total`
      | `item.facebookPost.insights.interactions.unique`
      | `item.facebookPost.insights.interactions.total`
      | `item.facebookPost.insights.reactions.like`
      | `item.facebookPost.insights.reactions.love`
      | `item.facebookPost.insights.reactions.wow`
      | `item.facebookPost.insights.reactions.haha`
      | `item.facebookPost.insights.reactions.sad`
      | `item.facebookPost.insights.reactions.anger`
      | `item.facebookPost.insights.activity.share`
      | `item.facebookPost.insights.activity.like`
      | `item.facebookPost.insights.activity.comment`
      | `item.youtubeVideo.notifySubscribers`
      | `item.youtubeVideo.insights.views`
      | `item.youtubeVideo.insights.comments`
      | `item.youtubeVideo.insights.likes`
      | `item.youtubeVideo.insights.dislikes`
      | `item.youtubeVideo.insights.shares`
      | `item.youtubeShort.notifySubscribers`
      | `item.linkedinPost.insights.uniqueImpressions`
      | `item.linkedinPost.insights.impression`
      | `item.linkedinPost.insights.shares`
      | `item.linkedinPost.insights.engagement`
      | `item.linkedinPost.insights.clicks`
      | `item.linkedinPost.insights.likes`
      | `item.linkedinPost.insights.comments`
      | `item.twitterPost.insights.retweets`
      | `item.twitterPost.insights.replies`
      | `item.twitterPost.insights.likes`
      | `item.twitterPost.insights.quotes`
      | `item.twitterPost.insights.bookmarks`
      | `item.twitterPost.insights.impressions`
      | `item.pinterestPost.insights.impressions`
      | `item.pinterestPost.insights.clicks`
      | `item.pinterestPost.insights.saves`
      | `item.pinterestPost.insights.comments`
      | `item.pinterestPost.insights.reactions`
      | `item.gbpPost.callToAction.actionType`
      | `item.gbpPost.insights.shares`
      | `item.tiktokVideo.privacyLevel`
      | `item.tiktokVideo.disableComment`
      | `item.tiktokVideo.disableDuet`
      | `item.tiktokVideo.disableStitch`
      | `item.tiktokVideo.brandContentToggle`
      | `item.tiktokVideo.brandOrganicToggle`
      | `item.tiktokVideo.insights.views`
      | `item.tiktokVideo.insights.likes`
      | `item.tiktokVideo.insights.comments`
      | `item.tiktokVideo.insights.shares`
      | `item.tiktokPhoto.privacyLevel`
      | `item.tiktokPhoto.disableComment`
      | `item.tiktokPhoto.autoAddMusic`
      | `item.tiktokPhoto.brandContentToggle`
      | `item.tiktokPhoto.brandOrganicToggle`
      | `item.channel.name`
      | `item.type`
      | `item.status`
      | `item.siteAnalytics.traffic`
      | `item.siteAnalytics.pageView`
      | `item.siteAnalytics.siteBounceRate`
      | `item.siteAnalytics.revenue`
      | `item.siteAnalytics.conversionFunnel.cartViewsSessions`
      | `item.siteAnalytics.conversionFunnel.checkOutSessions`
      | `item.siteAnalytics.conversionFunnel.storeOrdersSessions`
      | `item.siteAnalytics.conversionFunnel.bookingsConfirmed`
      | `item.insightsSummary.impressions`
      | `item.insightsSummary.interactions`
      | `item.insightsSummary.impressionsTotal`
      | `item.suggested`
      | `item.external`
      | `item.siteAssetRef.siteAssetRefType`
      | `item.siteAssetRef.name`,
      5
    > & {
      __applicationErrorsType?: PublishItemApplicationErrors;
    }
  >;
}

export function publishItemById(
  httpClient: HttpClient
): PublishItemByIdSignature {
  return (_id: string, options?: PublishItemByIdOptions) =>
    universalPublishItemById(
      _id,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface PublishItemByIdSignature {
  /**
   * Publishes a previously created item by its `id`.
   *
   * The item can be a draft or a scheduled item. To schedule the item for a future date, set `scheduledDate`; otherwise it's published immediately.
   *
   * Published items appear on the site's Social Media Marketing page in the dashboard.
   * @param - ID of the item to publish.
   */
  (_id: string, options?: PublishItemByIdOptions): Promise<
    NonNullablePaths<
      PublishItemByIdResponse,
      | `item.instagramPost.mediaWrapper.media`
      | `item.instagramPost.mediaWrapper.media.${number}.type`
      | `item.instagramPost.insights.likes`
      | `item.instagramPost.insights.engagement`
      | `item.instagramPost.insights.impressions`
      | `item.instagramPost.insights.plays`
      | `item.instagramPost.insights.reach`
      | `item.instagramPost.insights.comments`
      | `item.instagramPost.insights.shares`
      | `item.instagramPost.insights.saved`
      | `item.instagramPost.insights.totalInteractions`
      | `item.instagramStory.insights.impressions`
      | `item.instagramStory.insights.reach`
      | `item.instagramStory.insights.replies`
      | `item.facebookPost.insights.impressions.unique`
      | `item.facebookPost.insights.impressions.total`
      | `item.facebookPost.insights.interactions.unique`
      | `item.facebookPost.insights.interactions.total`
      | `item.facebookPost.insights.reactions.like`
      | `item.facebookPost.insights.reactions.love`
      | `item.facebookPost.insights.reactions.wow`
      | `item.facebookPost.insights.reactions.haha`
      | `item.facebookPost.insights.reactions.sad`
      | `item.facebookPost.insights.reactions.anger`
      | `item.facebookPost.insights.activity.share`
      | `item.facebookPost.insights.activity.like`
      | `item.facebookPost.insights.activity.comment`
      | `item.youtubeVideo.notifySubscribers`
      | `item.youtubeVideo.insights.views`
      | `item.youtubeVideo.insights.comments`
      | `item.youtubeVideo.insights.likes`
      | `item.youtubeVideo.insights.dislikes`
      | `item.youtubeVideo.insights.shares`
      | `item.youtubeShort.notifySubscribers`
      | `item.linkedinPost.insights.uniqueImpressions`
      | `item.linkedinPost.insights.impression`
      | `item.linkedinPost.insights.shares`
      | `item.linkedinPost.insights.engagement`
      | `item.linkedinPost.insights.clicks`
      | `item.linkedinPost.insights.likes`
      | `item.linkedinPost.insights.comments`
      | `item.twitterPost.insights.retweets`
      | `item.twitterPost.insights.replies`
      | `item.twitterPost.insights.likes`
      | `item.twitterPost.insights.quotes`
      | `item.twitterPost.insights.bookmarks`
      | `item.twitterPost.insights.impressions`
      | `item.pinterestPost.insights.impressions`
      | `item.pinterestPost.insights.clicks`
      | `item.pinterestPost.insights.saves`
      | `item.pinterestPost.insights.comments`
      | `item.pinterestPost.insights.reactions`
      | `item.gbpPost.callToAction.actionType`
      | `item.gbpPost.insights.shares`
      | `item.tiktokVideo.privacyLevel`
      | `item.tiktokVideo.disableComment`
      | `item.tiktokVideo.disableDuet`
      | `item.tiktokVideo.disableStitch`
      | `item.tiktokVideo.brandContentToggle`
      | `item.tiktokVideo.brandOrganicToggle`
      | `item.tiktokVideo.insights.views`
      | `item.tiktokVideo.insights.likes`
      | `item.tiktokVideo.insights.comments`
      | `item.tiktokVideo.insights.shares`
      | `item.tiktokPhoto.privacyLevel`
      | `item.tiktokPhoto.disableComment`
      | `item.tiktokPhoto.autoAddMusic`
      | `item.tiktokPhoto.brandContentToggle`
      | `item.tiktokPhoto.brandOrganicToggle`
      | `item.channel.name`
      | `item.type`
      | `item.status`
      | `item.siteAnalytics.traffic`
      | `item.siteAnalytics.pageView`
      | `item.siteAnalytics.siteBounceRate`
      | `item.siteAnalytics.revenue`
      | `item.siteAnalytics.conversionFunnel.cartViewsSessions`
      | `item.siteAnalytics.conversionFunnel.checkOutSessions`
      | `item.siteAnalytics.conversionFunnel.storeOrdersSessions`
      | `item.siteAnalytics.conversionFunnel.bookingsConfirmed`
      | `item.insightsSummary.impressions`
      | `item.insightsSummary.interactions`
      | `item.insightsSummary.impressionsTotal`
      | `item.suggested`
      | `item.external`
      | `item.siteAssetRef.siteAssetRefType`
      | `item.siteAssetRef.name`,
      5
    > & {
      __applicationErrorsType?: PublishItemByIdApplicationErrors;
    }
  >;
}

export function createDraftItem(
  httpClient: HttpClient
): CreateDraftItemSignature {
  return (
    options?: NonNullablePaths<CreateDraftItemOptions, `item.channel`, 1>
  ) =>
    universalCreateDraftItem(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateDraftItemSignature {
  /**
   * Creates a draft item.
   *
   * A draft is an item that's saved but not yet published, with a `status` of `DRAFT`. Drafts appear on the site's Social Media Marketing page in the dashboard, where they can be reviewed and published. To publish a draft, call Publish Item By ID.
   *
   * The item's `type` and content must match a combination supported by the target channel. See `item.type` for the supported combinations.
   * @returns Created draft item.
   */
  (
    options?: NonNullablePaths<CreateDraftItemOptions, `item.channel`, 1>
  ): Promise<
    NonNullablePaths<
      Item,
      | `instagramPost.mediaWrapper.media`
      | `instagramPost.mediaWrapper.media.${number}.type`
      | `instagramPost.insights.likes`
      | `instagramPost.insights.engagement`
      | `instagramPost.insights.impressions`
      | `instagramPost.insights.plays`
      | `instagramPost.insights.reach`
      | `instagramPost.insights.comments`
      | `instagramPost.insights.shares`
      | `instagramPost.insights.saved`
      | `instagramPost.insights.totalInteractions`
      | `instagramStory.insights.impressions`
      | `instagramStory.insights.reach`
      | `instagramStory.insights.replies`
      | `facebookPost.insights.impressions.unique`
      | `facebookPost.insights.impressions.total`
      | `facebookPost.insights.interactions.unique`
      | `facebookPost.insights.interactions.total`
      | `facebookPost.insights.reactions.like`
      | `facebookPost.insights.reactions.love`
      | `facebookPost.insights.reactions.wow`
      | `facebookPost.insights.reactions.haha`
      | `facebookPost.insights.reactions.sad`
      | `facebookPost.insights.reactions.anger`
      | `facebookPost.insights.activity.share`
      | `facebookPost.insights.activity.like`
      | `facebookPost.insights.activity.comment`
      | `youtubeVideo.notifySubscribers`
      | `youtubeVideo.insights.views`
      | `youtubeVideo.insights.comments`
      | `youtubeVideo.insights.likes`
      | `youtubeVideo.insights.dislikes`
      | `youtubeVideo.insights.shares`
      | `youtubeShort.notifySubscribers`
      | `linkedinPost.insights.uniqueImpressions`
      | `linkedinPost.insights.impression`
      | `linkedinPost.insights.shares`
      | `linkedinPost.insights.engagement`
      | `linkedinPost.insights.clicks`
      | `linkedinPost.insights.likes`
      | `linkedinPost.insights.comments`
      | `twitterPost.insights.retweets`
      | `twitterPost.insights.replies`
      | `twitterPost.insights.likes`
      | `twitterPost.insights.quotes`
      | `twitterPost.insights.bookmarks`
      | `twitterPost.insights.impressions`
      | `pinterestPost.insights.impressions`
      | `pinterestPost.insights.clicks`
      | `pinterestPost.insights.saves`
      | `pinterestPost.insights.comments`
      | `pinterestPost.insights.reactions`
      | `gbpPost.callToAction.actionType`
      | `gbpPost.insights.shares`
      | `tiktokVideo.privacyLevel`
      | `tiktokVideo.disableComment`
      | `tiktokVideo.disableDuet`
      | `tiktokVideo.disableStitch`
      | `tiktokVideo.brandContentToggle`
      | `tiktokVideo.brandOrganicToggle`
      | `tiktokVideo.insights.views`
      | `tiktokVideo.insights.likes`
      | `tiktokVideo.insights.comments`
      | `tiktokVideo.insights.shares`
      | `tiktokPhoto.privacyLevel`
      | `tiktokPhoto.disableComment`
      | `tiktokPhoto.autoAddMusic`
      | `tiktokPhoto.brandContentToggle`
      | `tiktokPhoto.brandOrganicToggle`
      | `channel.name`
      | `type`
      | `status`
      | `siteAnalytics.traffic`
      | `siteAnalytics.pageView`
      | `siteAnalytics.siteBounceRate`
      | `siteAnalytics.revenue`
      | `siteAnalytics.conversionFunnel.cartViewsSessions`
      | `siteAnalytics.conversionFunnel.checkOutSessions`
      | `siteAnalytics.conversionFunnel.storeOrdersSessions`
      | `siteAnalytics.conversionFunnel.bookingsConfirmed`
      | `insightsSummary.impressions`
      | `insightsSummary.interactions`
      | `insightsSummary.impressionsTotal`
      | `suggested`
      | `external`
      | `siteAssetRef.siteAssetRefType`
      | `siteAssetRef.name`,
      4
    >
  >;
}

export function publishMultiStoryDraft(
  httpClient: HttpClient
): PublishMultiStoryDraftSignature {
  return (_id: string, options?: PublishMultiStoryDraftOptions) =>
    universalPublishMultiStoryDraft(
      _id,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface PublishMultiStoryDraftSignature {
  /**
   * Publishes a multi-story draft as a sequence of single-media story items.
   *
   * The draft's media list is split into individual story items that are published sequentially
   * in media order, stopping on the first failure. Each story item gets a derived `referenceId`
   * of `{draftReferenceId}:story:{index}` (the draft `id` is used when the draft has no `referenceId`).
   * Story items already published by a previous call are skipped, so the method can be safely retried
   * after a partial failure. The draft is deleted only after all story items are published.
   * To schedule the story sequence, set `schedulingInfo.scheduledDate`; otherwise it's published immediately.
   * @param - ID of the multi-story draft to publish.
   * @returns PublishMultiStoryDraftResponse
   */
  (_id: string, options?: PublishMultiStoryDraftOptions): Promise<
    NonNullablePaths<
      PublishMultiStoryDraftResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.youtubeVideo.notifySubscribers`
      | `results.${number}.item.youtubeShort.notifySubscribers`
      | `results.${number}.item.tiktokVideo.privacyLevel`
      | `results.${number}.item.tiktokVideo.disableComment`
      | `results.${number}.item.tiktokVideo.disableDuet`
      | `results.${number}.item.tiktokVideo.disableStitch`
      | `results.${number}.item.tiktokVideo.brandContentToggle`
      | `results.${number}.item.tiktokVideo.brandOrganicToggle`
      | `results.${number}.item.tiktokPhoto.privacyLevel`
      | `results.${number}.item.tiktokPhoto.disableComment`
      | `results.${number}.item.tiktokPhoto.autoAddMusic`
      | `results.${number}.item.tiktokPhoto.brandContentToggle`
      | `results.${number}.item.tiktokPhoto.brandOrganicToggle`
      | `results.${number}.item.channel.name`
      | `results.${number}.item.type`
      | `results.${number}.item.status`
      | `results.${number}.item.siteAnalytics.traffic`
      | `results.${number}.item.siteAnalytics.pageView`
      | `results.${number}.item.siteAnalytics.siteBounceRate`
      | `results.${number}.item.siteAnalytics.revenue`
      | `results.${number}.item.insightsSummary.impressions`
      | `results.${number}.item.insightsSummary.interactions`
      | `results.${number}.item.insightsSummary.impressionsTotal`
      | `results.${number}.item.suggested`
      | `results.${number}.item.external`
      | `results.${number}.item.siteAssetRef.siteAssetRefType`
      | `results.${number}.item.siteAssetRef.name`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      4
    > & {
      __applicationErrorsType?: PublishMultiStoryDraftApplicationErrors;
    }
  >;
}

export function getItem(httpClient: HttpClient): GetItemSignature {
  return (_id: string) =>
    universalGetItem(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface GetItemSignature {
  /**
   * Retrieves an item by ID.
   * @param - ID of the item to retrieve.
   * @returns Retrieved item.
   */
  (_id: string): Promise<
    NonNullablePaths<
      Item,
      | `instagramPost.mediaWrapper.media`
      | `instagramPost.mediaWrapper.media.${number}.type`
      | `instagramPost.insights.likes`
      | `instagramPost.insights.engagement`
      | `instagramPost.insights.impressions`
      | `instagramPost.insights.plays`
      | `instagramPost.insights.reach`
      | `instagramPost.insights.comments`
      | `instagramPost.insights.shares`
      | `instagramPost.insights.saved`
      | `instagramPost.insights.totalInteractions`
      | `instagramStory.insights.impressions`
      | `instagramStory.insights.reach`
      | `instagramStory.insights.replies`
      | `facebookPost.insights.impressions.unique`
      | `facebookPost.insights.impressions.total`
      | `facebookPost.insights.interactions.unique`
      | `facebookPost.insights.interactions.total`
      | `facebookPost.insights.reactions.like`
      | `facebookPost.insights.reactions.love`
      | `facebookPost.insights.reactions.wow`
      | `facebookPost.insights.reactions.haha`
      | `facebookPost.insights.reactions.sad`
      | `facebookPost.insights.reactions.anger`
      | `facebookPost.insights.activity.share`
      | `facebookPost.insights.activity.like`
      | `facebookPost.insights.activity.comment`
      | `youtubeVideo.notifySubscribers`
      | `youtubeVideo.insights.views`
      | `youtubeVideo.insights.comments`
      | `youtubeVideo.insights.likes`
      | `youtubeVideo.insights.dislikes`
      | `youtubeVideo.insights.shares`
      | `youtubeShort.notifySubscribers`
      | `linkedinPost.insights.uniqueImpressions`
      | `linkedinPost.insights.impression`
      | `linkedinPost.insights.shares`
      | `linkedinPost.insights.engagement`
      | `linkedinPost.insights.clicks`
      | `linkedinPost.insights.likes`
      | `linkedinPost.insights.comments`
      | `twitterPost.insights.retweets`
      | `twitterPost.insights.replies`
      | `twitterPost.insights.likes`
      | `twitterPost.insights.quotes`
      | `twitterPost.insights.bookmarks`
      | `twitterPost.insights.impressions`
      | `pinterestPost.insights.impressions`
      | `pinterestPost.insights.clicks`
      | `pinterestPost.insights.saves`
      | `pinterestPost.insights.comments`
      | `pinterestPost.insights.reactions`
      | `gbpPost.callToAction.actionType`
      | `gbpPost.insights.shares`
      | `tiktokVideo.privacyLevel`
      | `tiktokVideo.disableComment`
      | `tiktokVideo.disableDuet`
      | `tiktokVideo.disableStitch`
      | `tiktokVideo.brandContentToggle`
      | `tiktokVideo.brandOrganicToggle`
      | `tiktokVideo.insights.views`
      | `tiktokVideo.insights.likes`
      | `tiktokVideo.insights.comments`
      | `tiktokVideo.insights.shares`
      | `tiktokPhoto.privacyLevel`
      | `tiktokPhoto.disableComment`
      | `tiktokPhoto.autoAddMusic`
      | `tiktokPhoto.brandContentToggle`
      | `tiktokPhoto.brandOrganicToggle`
      | `channel.name`
      | `type`
      | `status`
      | `siteAnalytics.traffic`
      | `siteAnalytics.pageView`
      | `siteAnalytics.siteBounceRate`
      | `siteAnalytics.revenue`
      | `siteAnalytics.conversionFunnel.cartViewsSessions`
      | `siteAnalytics.conversionFunnel.checkOutSessions`
      | `siteAnalytics.conversionFunnel.storeOrdersSessions`
      | `siteAnalytics.conversionFunnel.bookingsConfirmed`
      | `insightsSummary.impressions`
      | `insightsSummary.interactions`
      | `insightsSummary.impressionsTotal`
      | `suggested`
      | `external`
      | `siteAssetRef.siteAssetRefType`
      | `siteAssetRef.name`,
      4
    >
  >;
}

export function getItems(httpClient: HttpClient): GetItemsSignature {
  return (ids: string[]) =>
    universalGetItems(
      ids,
      // @ts-ignore
      { httpClient }
    );
}

interface GetItemsSignature {
  /**
   * Retrieves a list of items by their IDs.
   * @param - IDs of the items to retrieve.
   */
  (ids: string[]): Promise<
    NonNullablePaths<
      GetItemsResponse,
      | `items`
      | `items.${number}.instagramPost.insights.likes`
      | `items.${number}.instagramPost.insights.engagement`
      | `items.${number}.instagramPost.insights.impressions`
      | `items.${number}.instagramPost.insights.plays`
      | `items.${number}.instagramPost.insights.reach`
      | `items.${number}.instagramPost.insights.comments`
      | `items.${number}.instagramPost.insights.shares`
      | `items.${number}.instagramPost.insights.saved`
      | `items.${number}.instagramPost.insights.totalInteractions`
      | `items.${number}.instagramStory.insights.impressions`
      | `items.${number}.instagramStory.insights.reach`
      | `items.${number}.instagramStory.insights.replies`
      | `items.${number}.youtubeVideo.notifySubscribers`
      | `items.${number}.youtubeVideo.insights.views`
      | `items.${number}.youtubeVideo.insights.comments`
      | `items.${number}.youtubeVideo.insights.likes`
      | `items.${number}.youtubeVideo.insights.dislikes`
      | `items.${number}.youtubeVideo.insights.shares`
      | `items.${number}.youtubeShort.notifySubscribers`
      | `items.${number}.linkedinPost.insights.uniqueImpressions`
      | `items.${number}.linkedinPost.insights.impression`
      | `items.${number}.linkedinPost.insights.shares`
      | `items.${number}.linkedinPost.insights.engagement`
      | `items.${number}.linkedinPost.insights.clicks`
      | `items.${number}.linkedinPost.insights.likes`
      | `items.${number}.linkedinPost.insights.comments`
      | `items.${number}.twitterPost.insights.retweets`
      | `items.${number}.twitterPost.insights.replies`
      | `items.${number}.twitterPost.insights.likes`
      | `items.${number}.twitterPost.insights.quotes`
      | `items.${number}.twitterPost.insights.bookmarks`
      | `items.${number}.twitterPost.insights.impressions`
      | `items.${number}.pinterestPost.insights.impressions`
      | `items.${number}.pinterestPost.insights.clicks`
      | `items.${number}.pinterestPost.insights.saves`
      | `items.${number}.pinterestPost.insights.comments`
      | `items.${number}.pinterestPost.insights.reactions`
      | `items.${number}.gbpPost.callToAction.actionType`
      | `items.${number}.gbpPost.insights.shares`
      | `items.${number}.tiktokVideo.privacyLevel`
      | `items.${number}.tiktokVideo.disableComment`
      | `items.${number}.tiktokVideo.disableDuet`
      | `items.${number}.tiktokVideo.disableStitch`
      | `items.${number}.tiktokVideo.brandContentToggle`
      | `items.${number}.tiktokVideo.brandOrganicToggle`
      | `items.${number}.tiktokVideo.insights.views`
      | `items.${number}.tiktokVideo.insights.likes`
      | `items.${number}.tiktokVideo.insights.comments`
      | `items.${number}.tiktokVideo.insights.shares`
      | `items.${number}.tiktokPhoto.privacyLevel`
      | `items.${number}.tiktokPhoto.disableComment`
      | `items.${number}.tiktokPhoto.autoAddMusic`
      | `items.${number}.tiktokPhoto.brandContentToggle`
      | `items.${number}.tiktokPhoto.brandOrganicToggle`
      | `items.${number}.channel.name`
      | `items.${number}.type`
      | `items.${number}.status`
      | `items.${number}.siteAnalytics.traffic`
      | `items.${number}.siteAnalytics.pageView`
      | `items.${number}.siteAnalytics.siteBounceRate`
      | `items.${number}.siteAnalytics.revenue`
      | `items.${number}.siteAnalytics.conversionFunnel.cartViewsSessions`
      | `items.${number}.siteAnalytics.conversionFunnel.checkOutSessions`
      | `items.${number}.siteAnalytics.conversionFunnel.storeOrdersSessions`
      | `items.${number}.siteAnalytics.conversionFunnel.bookingsConfirmed`
      | `items.${number}.insightsSummary.impressions`
      | `items.${number}.insightsSummary.interactions`
      | `items.${number}.insightsSummary.impressionsTotal`
      | `items.${number}.suggested`
      | `items.${number}.external`
      | `items.${number}.siteAssetRef.siteAssetRefType`
      | `items.${number}.siteAssetRef.name`,
      4
    >
  >;
}

export function queryItems(httpClient: HttpClient): QueryItemsSignature {
  return (query: Query) =>
    universalQueryItems(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryItemsSignature {
  /**
   * Retrieves a list of items, given the provided paging, filtering, and sorting.
   *
   * Query Items runs with these defaults, which you can override:
   * - `paging.limit` is `50`
   * - `paging.offset` is `0`
   *
   * Query Items supports a fixed set of filterable fields:
   * - `status`: `PUBLISHED`, `SCHEDULED`, `DRAFT`, `FAILED`, `CANCELED`, `PROCESSING`, or `IN_QUEUE`.
   * - `channel`: a channel name, such as `FACEBOOK` or `INSTAGRAM`.
   * - `type`: `POST`, `STORY`, `REEL`, `VIDEO`, or `SHORT`.
   * - `mediaType`: `IMAGE` or `VIDEO`.
   * - `suggested`: `true` or `false`.
   * - `ids`: a list of item IDs.
   *
   * Each of these supports the `$eq` and `$in` operators, and a bare value as shorthand for `$eq`, except
   * for `ids`, which supports `$in` only. Enum values are case insensitive. A value that the API doesn't
   * define matches no items rather than returning an error, and a field that isn't listed above is ignored
   * rather than rejected, so a filter on an unsupported field returns the same items as no filter at all.
   *
   * Sorting is supported on these fields:
   * - `date`: the default. Newest first, unless you specify `order`.
   * - `impressionsTotal`, `impressions`, `interactions`, and `siteVisits`: require the site to have post
   * insights. Without them, the items are sorted by `date` instead, in whichever direction you
   * specified.
   *
   * To learn about working with Query methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language), [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging), and [Field Projection](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-field-projection).
   * @param - Query options, including paging, filtering, and sorting.
   */
  (query: Query): Promise<
    NonNullablePaths<
      QueryItemsResponse,
      | `items`
      | `items.${number}.instagramPost.insights.likes`
      | `items.${number}.instagramPost.insights.engagement`
      | `items.${number}.instagramPost.insights.impressions`
      | `items.${number}.instagramPost.insights.plays`
      | `items.${number}.instagramPost.insights.reach`
      | `items.${number}.instagramPost.insights.comments`
      | `items.${number}.instagramPost.insights.shares`
      | `items.${number}.instagramPost.insights.saved`
      | `items.${number}.instagramPost.insights.totalInteractions`
      | `items.${number}.instagramStory.insights.impressions`
      | `items.${number}.instagramStory.insights.reach`
      | `items.${number}.instagramStory.insights.replies`
      | `items.${number}.youtubeVideo.notifySubscribers`
      | `items.${number}.youtubeVideo.insights.views`
      | `items.${number}.youtubeVideo.insights.comments`
      | `items.${number}.youtubeVideo.insights.likes`
      | `items.${number}.youtubeVideo.insights.dislikes`
      | `items.${number}.youtubeVideo.insights.shares`
      | `items.${number}.youtubeShort.notifySubscribers`
      | `items.${number}.linkedinPost.insights.uniqueImpressions`
      | `items.${number}.linkedinPost.insights.impression`
      | `items.${number}.linkedinPost.insights.shares`
      | `items.${number}.linkedinPost.insights.engagement`
      | `items.${number}.linkedinPost.insights.clicks`
      | `items.${number}.linkedinPost.insights.likes`
      | `items.${number}.linkedinPost.insights.comments`
      | `items.${number}.twitterPost.insights.retweets`
      | `items.${number}.twitterPost.insights.replies`
      | `items.${number}.twitterPost.insights.likes`
      | `items.${number}.twitterPost.insights.quotes`
      | `items.${number}.twitterPost.insights.bookmarks`
      | `items.${number}.twitterPost.insights.impressions`
      | `items.${number}.pinterestPost.insights.impressions`
      | `items.${number}.pinterestPost.insights.clicks`
      | `items.${number}.pinterestPost.insights.saves`
      | `items.${number}.pinterestPost.insights.comments`
      | `items.${number}.pinterestPost.insights.reactions`
      | `items.${number}.gbpPost.callToAction.actionType`
      | `items.${number}.gbpPost.insights.shares`
      | `items.${number}.tiktokVideo.privacyLevel`
      | `items.${number}.tiktokVideo.disableComment`
      | `items.${number}.tiktokVideo.disableDuet`
      | `items.${number}.tiktokVideo.disableStitch`
      | `items.${number}.tiktokVideo.brandContentToggle`
      | `items.${number}.tiktokVideo.brandOrganicToggle`
      | `items.${number}.tiktokVideo.insights.views`
      | `items.${number}.tiktokVideo.insights.likes`
      | `items.${number}.tiktokVideo.insights.comments`
      | `items.${number}.tiktokVideo.insights.shares`
      | `items.${number}.tiktokPhoto.privacyLevel`
      | `items.${number}.tiktokPhoto.disableComment`
      | `items.${number}.tiktokPhoto.autoAddMusic`
      | `items.${number}.tiktokPhoto.brandContentToggle`
      | `items.${number}.tiktokPhoto.brandOrganicToggle`
      | `items.${number}.channel.name`
      | `items.${number}.type`
      | `items.${number}.status`
      | `items.${number}.siteAnalytics.traffic`
      | `items.${number}.siteAnalytics.pageView`
      | `items.${number}.siteAnalytics.siteBounceRate`
      | `items.${number}.siteAnalytics.revenue`
      | `items.${number}.siteAnalytics.conversionFunnel.cartViewsSessions`
      | `items.${number}.siteAnalytics.conversionFunnel.checkOutSessions`
      | `items.${number}.siteAnalytics.conversionFunnel.storeOrdersSessions`
      | `items.${number}.siteAnalytics.conversionFunnel.bookingsConfirmed`
      | `items.${number}.insightsSummary.impressions`
      | `items.${number}.insightsSummary.interactions`
      | `items.${number}.insightsSummary.impressionsTotal`
      | `items.${number}.suggested`
      | `items.${number}.external`
      | `items.${number}.siteAssetRef.siteAssetRefType`
      | `items.${number}.siteAssetRef.name`,
      4
    >
  >;
}

export function cancelScheduledItem(
  httpClient: HttpClient
): CancelScheduledItemSignature {
  return (_id: string) =>
    universalCancelScheduledItem(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface CancelScheduledItemSignature {
  /**
   * Cancels the scheduling of a scheduled item.
   *
   * The item is no longer published at its scheduled time. Only an item with a `status` of `SCHEDULED` can be canceled.
   * @param - ID of the scheduled item to cancel.
   */
  (_id: string): Promise<
    NonNullablePaths<
      CancelScheduledItemResponse,
      | `item.instagramPost.mediaWrapper.media`
      | `item.instagramPost.mediaWrapper.media.${number}.type`
      | `item.instagramPost.insights.likes`
      | `item.instagramPost.insights.engagement`
      | `item.instagramPost.insights.impressions`
      | `item.instagramPost.insights.plays`
      | `item.instagramPost.insights.reach`
      | `item.instagramPost.insights.comments`
      | `item.instagramPost.insights.shares`
      | `item.instagramPost.insights.saved`
      | `item.instagramPost.insights.totalInteractions`
      | `item.instagramStory.insights.impressions`
      | `item.instagramStory.insights.reach`
      | `item.instagramStory.insights.replies`
      | `item.facebookPost.insights.impressions.unique`
      | `item.facebookPost.insights.impressions.total`
      | `item.facebookPost.insights.interactions.unique`
      | `item.facebookPost.insights.interactions.total`
      | `item.facebookPost.insights.reactions.like`
      | `item.facebookPost.insights.reactions.love`
      | `item.facebookPost.insights.reactions.wow`
      | `item.facebookPost.insights.reactions.haha`
      | `item.facebookPost.insights.reactions.sad`
      | `item.facebookPost.insights.reactions.anger`
      | `item.facebookPost.insights.activity.share`
      | `item.facebookPost.insights.activity.like`
      | `item.facebookPost.insights.activity.comment`
      | `item.youtubeVideo.notifySubscribers`
      | `item.youtubeVideo.insights.views`
      | `item.youtubeVideo.insights.comments`
      | `item.youtubeVideo.insights.likes`
      | `item.youtubeVideo.insights.dislikes`
      | `item.youtubeVideo.insights.shares`
      | `item.youtubeShort.notifySubscribers`
      | `item.linkedinPost.insights.uniqueImpressions`
      | `item.linkedinPost.insights.impression`
      | `item.linkedinPost.insights.shares`
      | `item.linkedinPost.insights.engagement`
      | `item.linkedinPost.insights.clicks`
      | `item.linkedinPost.insights.likes`
      | `item.linkedinPost.insights.comments`
      | `item.twitterPost.insights.retweets`
      | `item.twitterPost.insights.replies`
      | `item.twitterPost.insights.likes`
      | `item.twitterPost.insights.quotes`
      | `item.twitterPost.insights.bookmarks`
      | `item.twitterPost.insights.impressions`
      | `item.pinterestPost.insights.impressions`
      | `item.pinterestPost.insights.clicks`
      | `item.pinterestPost.insights.saves`
      | `item.pinterestPost.insights.comments`
      | `item.pinterestPost.insights.reactions`
      | `item.gbpPost.callToAction.actionType`
      | `item.gbpPost.insights.shares`
      | `item.tiktokVideo.privacyLevel`
      | `item.tiktokVideo.disableComment`
      | `item.tiktokVideo.disableDuet`
      | `item.tiktokVideo.disableStitch`
      | `item.tiktokVideo.brandContentToggle`
      | `item.tiktokVideo.brandOrganicToggle`
      | `item.tiktokVideo.insights.views`
      | `item.tiktokVideo.insights.likes`
      | `item.tiktokVideo.insights.comments`
      | `item.tiktokVideo.insights.shares`
      | `item.tiktokPhoto.privacyLevel`
      | `item.tiktokPhoto.disableComment`
      | `item.tiktokPhoto.autoAddMusic`
      | `item.tiktokPhoto.brandContentToggle`
      | `item.tiktokPhoto.brandOrganicToggle`
      | `item.channel.name`
      | `item.type`
      | `item.status`
      | `item.siteAnalytics.traffic`
      | `item.siteAnalytics.pageView`
      | `item.siteAnalytics.siteBounceRate`
      | `item.siteAnalytics.revenue`
      | `item.siteAnalytics.conversionFunnel.cartViewsSessions`
      | `item.siteAnalytics.conversionFunnel.checkOutSessions`
      | `item.siteAnalytics.conversionFunnel.storeOrdersSessions`
      | `item.siteAnalytics.conversionFunnel.bookingsConfirmed`
      | `item.insightsSummary.impressions`
      | `item.insightsSummary.interactions`
      | `item.insightsSummary.impressionsTotal`
      | `item.suggested`
      | `item.external`
      | `item.siteAssetRef.siteAssetRefType`
      | `item.siteAssetRef.name`,
      5
    > & {
      __applicationErrorsType?: CancelScheduledItemApplicationErrors;
    }
  >;
}

export function updateItem(httpClient: HttpClient): UpdateItemSignature {
  return (_id: string, item: UpdateItem) =>
    universalUpdateItem(
      _id,
      item,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateItemSignature {
  /**
   * Updates an item.
   *
   * Provide the `id` of the item to update along with the fields to change. Only the fields you include are changed. To change a scheduled item's publish date, call Reschedule Item instead.
   * @param - Item ID.
   * @returns Updated item.
   */
  (_id: string, item: UpdateItem): Promise<
    NonNullablePaths<
      Item,
      | `instagramPost.mediaWrapper.media`
      | `instagramPost.mediaWrapper.media.${number}.type`
      | `instagramPost.insights.likes`
      | `instagramPost.insights.engagement`
      | `instagramPost.insights.impressions`
      | `instagramPost.insights.plays`
      | `instagramPost.insights.reach`
      | `instagramPost.insights.comments`
      | `instagramPost.insights.shares`
      | `instagramPost.insights.saved`
      | `instagramPost.insights.totalInteractions`
      | `instagramStory.insights.impressions`
      | `instagramStory.insights.reach`
      | `instagramStory.insights.replies`
      | `facebookPost.insights.impressions.unique`
      | `facebookPost.insights.impressions.total`
      | `facebookPost.insights.interactions.unique`
      | `facebookPost.insights.interactions.total`
      | `facebookPost.insights.reactions.like`
      | `facebookPost.insights.reactions.love`
      | `facebookPost.insights.reactions.wow`
      | `facebookPost.insights.reactions.haha`
      | `facebookPost.insights.reactions.sad`
      | `facebookPost.insights.reactions.anger`
      | `facebookPost.insights.activity.share`
      | `facebookPost.insights.activity.like`
      | `facebookPost.insights.activity.comment`
      | `youtubeVideo.notifySubscribers`
      | `youtubeVideo.insights.views`
      | `youtubeVideo.insights.comments`
      | `youtubeVideo.insights.likes`
      | `youtubeVideo.insights.dislikes`
      | `youtubeVideo.insights.shares`
      | `youtubeShort.notifySubscribers`
      | `linkedinPost.insights.uniqueImpressions`
      | `linkedinPost.insights.impression`
      | `linkedinPost.insights.shares`
      | `linkedinPost.insights.engagement`
      | `linkedinPost.insights.clicks`
      | `linkedinPost.insights.likes`
      | `linkedinPost.insights.comments`
      | `twitterPost.insights.retweets`
      | `twitterPost.insights.replies`
      | `twitterPost.insights.likes`
      | `twitterPost.insights.quotes`
      | `twitterPost.insights.bookmarks`
      | `twitterPost.insights.impressions`
      | `pinterestPost.insights.impressions`
      | `pinterestPost.insights.clicks`
      | `pinterestPost.insights.saves`
      | `pinterestPost.insights.comments`
      | `pinterestPost.insights.reactions`
      | `gbpPost.callToAction.actionType`
      | `gbpPost.insights.shares`
      | `tiktokVideo.privacyLevel`
      | `tiktokVideo.disableComment`
      | `tiktokVideo.disableDuet`
      | `tiktokVideo.disableStitch`
      | `tiktokVideo.brandContentToggle`
      | `tiktokVideo.brandOrganicToggle`
      | `tiktokVideo.insights.views`
      | `tiktokVideo.insights.likes`
      | `tiktokVideo.insights.comments`
      | `tiktokVideo.insights.shares`
      | `tiktokPhoto.privacyLevel`
      | `tiktokPhoto.disableComment`
      | `tiktokPhoto.autoAddMusic`
      | `tiktokPhoto.brandContentToggle`
      | `tiktokPhoto.brandOrganicToggle`
      | `channel.name`
      | `type`
      | `status`
      | `siteAnalytics.traffic`
      | `siteAnalytics.pageView`
      | `siteAnalytics.siteBounceRate`
      | `siteAnalytics.revenue`
      | `siteAnalytics.conversionFunnel.cartViewsSessions`
      | `siteAnalytics.conversionFunnel.checkOutSessions`
      | `siteAnalytics.conversionFunnel.storeOrdersSessions`
      | `siteAnalytics.conversionFunnel.bookingsConfirmed`
      | `insightsSummary.impressions`
      | `insightsSummary.interactions`
      | `insightsSummary.impressionsTotal`
      | `suggested`
      | `external`
      | `siteAssetRef.siteAssetRefType`
      | `siteAssetRef.name`,
      4
    > & {
      __applicationErrorsType?: UpdateItemApplicationErrors;
    }
  >;
}

export function rescheduleItem(
  httpClient: HttpClient
): RescheduleItemSignature {
  return (_id: string, options?: RescheduleItemOptions) =>
    universalRescheduleItem(
      _id,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface RescheduleItemSignature {
  /**
   * Changes the scheduled publish date of a scheduled item.
   *
   * Only an item with a `status` of `SCHEDULED` can be rescheduled.
   * @param - ID of the scheduled item to reschedule.
   */
  (_id: string, options?: RescheduleItemOptions): Promise<
    NonNullablePaths<
      RescheduleItemResponse,
      | `item.instagramPost.mediaWrapper.media`
      | `item.instagramPost.mediaWrapper.media.${number}.type`
      | `item.instagramPost.insights.likes`
      | `item.instagramPost.insights.engagement`
      | `item.instagramPost.insights.impressions`
      | `item.instagramPost.insights.plays`
      | `item.instagramPost.insights.reach`
      | `item.instagramPost.insights.comments`
      | `item.instagramPost.insights.shares`
      | `item.instagramPost.insights.saved`
      | `item.instagramPost.insights.totalInteractions`
      | `item.instagramStory.insights.impressions`
      | `item.instagramStory.insights.reach`
      | `item.instagramStory.insights.replies`
      | `item.facebookPost.insights.impressions.unique`
      | `item.facebookPost.insights.impressions.total`
      | `item.facebookPost.insights.interactions.unique`
      | `item.facebookPost.insights.interactions.total`
      | `item.facebookPost.insights.reactions.like`
      | `item.facebookPost.insights.reactions.love`
      | `item.facebookPost.insights.reactions.wow`
      | `item.facebookPost.insights.reactions.haha`
      | `item.facebookPost.insights.reactions.sad`
      | `item.facebookPost.insights.reactions.anger`
      | `item.facebookPost.insights.activity.share`
      | `item.facebookPost.insights.activity.like`
      | `item.facebookPost.insights.activity.comment`
      | `item.youtubeVideo.notifySubscribers`
      | `item.youtubeVideo.insights.views`
      | `item.youtubeVideo.insights.comments`
      | `item.youtubeVideo.insights.likes`
      | `item.youtubeVideo.insights.dislikes`
      | `item.youtubeVideo.insights.shares`
      | `item.youtubeShort.notifySubscribers`
      | `item.linkedinPost.insights.uniqueImpressions`
      | `item.linkedinPost.insights.impression`
      | `item.linkedinPost.insights.shares`
      | `item.linkedinPost.insights.engagement`
      | `item.linkedinPost.insights.clicks`
      | `item.linkedinPost.insights.likes`
      | `item.linkedinPost.insights.comments`
      | `item.twitterPost.insights.retweets`
      | `item.twitterPost.insights.replies`
      | `item.twitterPost.insights.likes`
      | `item.twitterPost.insights.quotes`
      | `item.twitterPost.insights.bookmarks`
      | `item.twitterPost.insights.impressions`
      | `item.pinterestPost.insights.impressions`
      | `item.pinterestPost.insights.clicks`
      | `item.pinterestPost.insights.saves`
      | `item.pinterestPost.insights.comments`
      | `item.pinterestPost.insights.reactions`
      | `item.gbpPost.callToAction.actionType`
      | `item.gbpPost.insights.shares`
      | `item.tiktokVideo.privacyLevel`
      | `item.tiktokVideo.disableComment`
      | `item.tiktokVideo.disableDuet`
      | `item.tiktokVideo.disableStitch`
      | `item.tiktokVideo.brandContentToggle`
      | `item.tiktokVideo.brandOrganicToggle`
      | `item.tiktokVideo.insights.views`
      | `item.tiktokVideo.insights.likes`
      | `item.tiktokVideo.insights.comments`
      | `item.tiktokVideo.insights.shares`
      | `item.tiktokPhoto.privacyLevel`
      | `item.tiktokPhoto.disableComment`
      | `item.tiktokPhoto.autoAddMusic`
      | `item.tiktokPhoto.brandContentToggle`
      | `item.tiktokPhoto.brandOrganicToggle`
      | `item.channel.name`
      | `item.type`
      | `item.status`
      | `item.siteAnalytics.traffic`
      | `item.siteAnalytics.pageView`
      | `item.siteAnalytics.siteBounceRate`
      | `item.siteAnalytics.revenue`
      | `item.siteAnalytics.conversionFunnel.cartViewsSessions`
      | `item.siteAnalytics.conversionFunnel.checkOutSessions`
      | `item.siteAnalytics.conversionFunnel.storeOrdersSessions`
      | `item.siteAnalytics.conversionFunnel.bookingsConfirmed`
      | `item.insightsSummary.impressions`
      | `item.insightsSummary.interactions`
      | `item.insightsSummary.impressionsTotal`
      | `item.suggested`
      | `item.external`
      | `item.siteAssetRef.siteAssetRefType`
      | `item.siteAssetRef.name`,
      5
    > & {
      __applicationErrorsType?: RescheduleItemApplicationErrors;
    }
  >;
}

export function deleteItem(httpClient: HttpClient): DeleteItemSignature {
  return (_id: string) =>
    universalDeleteItem(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteItemSignature {
  /**
   * Deletes an item.
   * @returns DeleteItemResponse
   */
  (_id: string): Promise<
    void & {
      __applicationErrorsType?: DeleteItemApplicationErrors;
    }
  >;
}

export function bulkPublishItems(
  httpClient: HttpClient
): BulkPublishItemsSignature {
  return (
    items: NonNullablePaths<Item, `channel` | `channel.accountId`, 1>[],
    options?: BulkPublishItemsOptions
  ) =>
    universalBulkPublishItems(
      items,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkPublishItemsSignature {
  /**
   * Publishes multiple items in a single call.
   *
   * Each item is published independently. To publish a single item, call Publish Item.
   * @param - Items to publish.
   */
  (
    items: NonNullablePaths<Item, `channel` | `channel.accountId`, 1>[],
    options?: BulkPublishItemsOptions
  ): Promise<
    NonNullablePaths<
      BulkPublishItemsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.youtubeVideo.notifySubscribers`
      | `results.${number}.item.youtubeShort.notifySubscribers`
      | `results.${number}.item.tiktokVideo.privacyLevel`
      | `results.${number}.item.tiktokVideo.disableComment`
      | `results.${number}.item.tiktokVideo.disableDuet`
      | `results.${number}.item.tiktokVideo.disableStitch`
      | `results.${number}.item.tiktokVideo.brandContentToggle`
      | `results.${number}.item.tiktokVideo.brandOrganicToggle`
      | `results.${number}.item.tiktokPhoto.privacyLevel`
      | `results.${number}.item.tiktokPhoto.disableComment`
      | `results.${number}.item.tiktokPhoto.autoAddMusic`
      | `results.${number}.item.tiktokPhoto.brandContentToggle`
      | `results.${number}.item.tiktokPhoto.brandOrganicToggle`
      | `results.${number}.item.channel.name`
      | `results.${number}.item.type`
      | `results.${number}.item.status`
      | `results.${number}.item.siteAnalytics.traffic`
      | `results.${number}.item.siteAnalytics.pageView`
      | `results.${number}.item.siteAnalytics.siteBounceRate`
      | `results.${number}.item.siteAnalytics.revenue`
      | `results.${number}.item.insightsSummary.impressions`
      | `results.${number}.item.insightsSummary.interactions`
      | `results.${number}.item.insightsSummary.impressionsTotal`
      | `results.${number}.item.suggested`
      | `results.${number}.item.external`
      | `results.${number}.item.siteAssetRef.siteAssetRefType`
      | `results.${number}.item.siteAssetRef.name`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      4
    > & {
      __applicationErrorsType?: BulkPublishItemsApplicationErrors;
    }
  >;
}

export const onItemPublishFailed = /*#__PURE__*/ EventDefinition(
  'wix.promote.publisher.v1.item_item_publish_failed',
  true,
  (event: ItemPublishFailedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.item.publishedDate' },
            { path: 'data.item.facebookPost.insights.updatedDate' },
            { path: 'data.item.facebookReel.insights.updatedDate' },
            { path: 'data.item.facebookStory.insights.updatedDate' },
            { path: 'data.item.youtubeVideo.insights.updatedDate' },
            { path: 'data.item.youtubeShort.insights.updatedDate' },
            { path: 'data.item.linkedinPost.insights.updatedDate' },
            { path: 'data.item.twitterPost.insights.updatedDate' },
            { path: 'data.item.pinterestPost.insights.updatedDate' },
            { path: 'data.item.gbpPost.insights.updatedDate' },
            { path: 'data.item.tiktokVideo.insights.updatedDate' },
            { path: 'data.item.tiktokPhoto.insights.updatedDate' },
            { path: 'data.item.schedulingInfo.scheduledDate' },
            { path: 'data.item.schedulingInfo.schedulingTimestamp' },
            { path: 'data.item.siteAnalytics.updatedDate' },
            { path: 'data.item.insightsSummary.updatedDate' },
            { path: 'data.item.draftInfo.createdDate' },
            { path: 'data.item.draftInfo.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'data.item.linkedinPost.insights.engagement' },
            { path: 'data.item.siteAnalytics.siteBounceRate' },
            { path: 'data.item.siteAnalytics.revenue' },
          ],
        },
      ])
    )
)<ItemPublishFailedEnvelope>();
export const onItemPublishScheduled = /*#__PURE__*/ EventDefinition(
  'wix.promote.publisher.v1.item_item_publish_scheduled',
  true,
  (event: ItemPublishScheduledEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.item.publishedDate' },
            { path: 'data.item.facebookPost.insights.updatedDate' },
            { path: 'data.item.facebookReel.insights.updatedDate' },
            { path: 'data.item.facebookStory.insights.updatedDate' },
            { path: 'data.item.youtubeVideo.insights.updatedDate' },
            { path: 'data.item.youtubeShort.insights.updatedDate' },
            { path: 'data.item.linkedinPost.insights.updatedDate' },
            { path: 'data.item.twitterPost.insights.updatedDate' },
            { path: 'data.item.pinterestPost.insights.updatedDate' },
            { path: 'data.item.gbpPost.insights.updatedDate' },
            { path: 'data.item.tiktokVideo.insights.updatedDate' },
            { path: 'data.item.tiktokPhoto.insights.updatedDate' },
            { path: 'data.item.schedulingInfo.scheduledDate' },
            { path: 'data.item.schedulingInfo.schedulingTimestamp' },
            { path: 'data.item.siteAnalytics.updatedDate' },
            { path: 'data.item.insightsSummary.updatedDate' },
            { path: 'data.item.draftInfo.createdDate' },
            { path: 'data.item.draftInfo.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'data.item.linkedinPost.insights.engagement' },
            { path: 'data.item.siteAnalytics.siteBounceRate' },
            { path: 'data.item.siteAnalytics.revenue' },
          ],
        },
      ])
    )
)<ItemPublishScheduledEnvelope>();
export const onItemPublishStarted = /*#__PURE__*/ EventDefinition(
  'wix.promote.publisher.v1.item_item_publish_started',
  true,
  (event: ItemPublishStartedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.item.publishedDate' },
            { path: 'data.item.facebookPost.insights.updatedDate' },
            { path: 'data.item.facebookReel.insights.updatedDate' },
            { path: 'data.item.facebookStory.insights.updatedDate' },
            { path: 'data.item.youtubeVideo.insights.updatedDate' },
            { path: 'data.item.youtubeShort.insights.updatedDate' },
            { path: 'data.item.linkedinPost.insights.updatedDate' },
            { path: 'data.item.twitterPost.insights.updatedDate' },
            { path: 'data.item.pinterestPost.insights.updatedDate' },
            { path: 'data.item.gbpPost.insights.updatedDate' },
            { path: 'data.item.tiktokVideo.insights.updatedDate' },
            { path: 'data.item.tiktokPhoto.insights.updatedDate' },
            { path: 'data.item.schedulingInfo.scheduledDate' },
            { path: 'data.item.schedulingInfo.schedulingTimestamp' },
            { path: 'data.item.siteAnalytics.updatedDate' },
            { path: 'data.item.insightsSummary.updatedDate' },
            { path: 'data.item.draftInfo.createdDate' },
            { path: 'data.item.draftInfo.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'data.item.linkedinPost.insights.engagement' },
            { path: 'data.item.siteAnalytics.siteBounceRate' },
            { path: 'data.item.siteAnalytics.revenue' },
          ],
        },
      ])
    )
)<ItemPublishStartedEnvelope>();
export const onItemPublished = /*#__PURE__*/ EventDefinition(
  'wix.promote.publisher.v1.item_item_published',
  true,
  (event: ItemPublishedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.item.publishedDate' },
            { path: 'data.item.facebookPost.insights.updatedDate' },
            { path: 'data.item.facebookReel.insights.updatedDate' },
            { path: 'data.item.facebookStory.insights.updatedDate' },
            { path: 'data.item.youtubeVideo.insights.updatedDate' },
            { path: 'data.item.youtubeShort.insights.updatedDate' },
            { path: 'data.item.linkedinPost.insights.updatedDate' },
            { path: 'data.item.twitterPost.insights.updatedDate' },
            { path: 'data.item.pinterestPost.insights.updatedDate' },
            { path: 'data.item.gbpPost.insights.updatedDate' },
            { path: 'data.item.tiktokVideo.insights.updatedDate' },
            { path: 'data.item.tiktokPhoto.insights.updatedDate' },
            { path: 'data.item.schedulingInfo.scheduledDate' },
            { path: 'data.item.schedulingInfo.schedulingTimestamp' },
            { path: 'data.item.siteAnalytics.updatedDate' },
            { path: 'data.item.insightsSummary.updatedDate' },
            { path: 'data.item.draftInfo.createdDate' },
            { path: 'data.item.draftInfo.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'data.item.linkedinPost.insights.engagement' },
            { path: 'data.item.siteAnalytics.siteBounceRate' },
            { path: 'data.item.siteAnalytics.revenue' },
          ],
        },
      ])
    )
)<ItemPublishedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  AddressLocation,
  ApplicationError,
  AssetReference,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkItemsResult,
  BulkPublishItemsOptions,
  BulkPublishItemsPolicy,
  BulkPublishItemsRequest,
  BulkPublishItemsResponse,
  CancelScheduledItemRequest,
  CancelScheduledItemResponse,
  Channel,
  ChannelName,
  CommentTrigger,
  ConsumerInfo,
  ConsumerType,
  ConversionFunnel,
  CreateDraftItemOptions,
  CreateDraftItemRequest,
  CreateDraftItemResponse,
  DeleteItemRequest,
  DeleteItemResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  DraftInfo,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  FacebokActivity,
  FacebookImpressions,
  FacebookInsights,
  FacebookInteractions,
  FacebookPost,
  FacebookPostUrlOneOf,
  FacebookReactions,
  FacebookReel,
  FacebookStory,
  GbpInsights,
  GbpPost,
  GbpPostCallToAction,
  GbpPostCallToActionType,
  GetItemRequest,
  GetItemResponse,
  GetItemsRequest,
  GetItemsResponse,
  GetOrCreateWixelBindingByFileRequest,
  GetOrCreateWixelBindingByFileResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  InsightsSummary,
  InstagramInsights,
  InstagramPost,
  InstagramPostUrlOneOf,
  InstagramStory,
  InstagramStoryInsights,
  IsEligibleForSchedulingRequest,
  IsEligibleForSchedulingResponse,
  Item,
  ItemAutomation,
  ItemAutomationActionOneOf,
  ItemAutomationTriggerOneOf,
  ItemAutomationsWrapper,
  ItemDataOneOf,
  ItemExtras,
  ItemMetadata,
  ItemPublishFailed,
  ItemPublishFailedEnvelope,
  ItemPublishScheduled,
  ItemPublishScheduledEnvelope,
  ItemPublishStarted,
  ItemPublishStartedEnvelope,
  ItemPublished,
  ItemPublishedEnvelope,
  ItemType,
  LinkMetadata,
  LinkedinPost,
  LinkedinPostInsights,
  LinkedinPostUrlOneOf,
  Media,
  MediaType,
  MediaWrapper,
  MessageEnvelope,
  Paging,
  PagingMetadata,
  PinterestInsights,
  PinterestPost,
  PublicationErrorDetails,
  PublishDraftRequest,
  PublishDraftResponse,
  PublishItemByIdOptions,
  PublishItemByIdRequest,
  PublishItemByIdResponse,
  PublishItemOptions,
  PublishItemRequest,
  PublishItemResponse,
  PublishMultiStoryDraftOptions,
  PublishMultiStoryDraftRequest,
  PublishMultiStoryDraftResponse,
  PublishingTarget,
  Query,
  QueryItemsRequest,
  QueryItemsResponse,
  ReplyToCommentAction,
  RescheduleItemOptions,
  RescheduleItemRequest,
  RescheduleItemResponse,
  RestoreInfo,
  SchedulingInfo,
  SchedulingRestrictedReason,
  SendPrivateReplyAction,
  SiteAnalytics,
  SortOrder,
  Sorting,
  Status,
  TikTokInsights,
  TikTokPhoto,
  TikTokPrivacyLevel,
  TikTokVideo,
  TwitterInsights,
  TwitterPost,
  TwitterPostUrlOneOf,
  Type,
  UpdateItem,
  UpdateItemRequest,
  UpdateItemResponse,
  WebhookIdentityType,
  YouTubeShort,
  YouTubeVideo,
  YoutubeInsights,
} from './promote-publisher-v1-item-items.universal.js';
